/**
 * OrgFilterBar — shared Industry / Region / Company Size filter pills
 * used by cross-org benchmarking pages (Org Benchmarks, PE
 * Benchmarking, future industry/region surfaces).
 *
 * Keeps the three selects + "Clear filters" shortcut consistent and
 * wired to the same data shape everywhere. Pair with the
 * `useOrgAnonymizer` hook which emits matching `availableRegions` /
 * `availableSizes` arrays.
 */
import React from 'react';
import { sizeLabel } from '../../hooks/useOrgAnonymizer';

export function OrgFilterBar({
  industries = [],
  availableRegions = [],
  availableSizes = [],
  industryValue = 'all',
  onIndustryChange,
  regionValue = 'all',
  onRegionChange,
  sizeValue = 'all',
  onSizeChange,
  testIdPrefix = 'benchmark',
}) {
  const tid = (suffix) => `${testIdPrefix}-${suffix}`;
  const anyActive =
    industryValue !== 'all' || regionValue !== 'all' || sizeValue !== 'all';

  const clear = () => {
    onIndustryChange?.('all');
    onRegionChange?.('all');
    onSizeChange?.('all');
  };

  const SELECT_CLASS =
    'text-xs rounded-md border border-slate-200 bg-white px-2 py-1.5 ' +
    'focus:outline-none focus:ring-1 focus:ring-blue-500 ' +
    'disabled:bg-slate-50 disabled:text-slate-400';

  return (
    <div
      className="flex items-center gap-3 flex-wrap"
      data-testid={tid('filters-row')}
    >
      <div className="flex items-center gap-2">
        <label
          htmlFor={tid('industry-filter')}
          className="text-xs font-medium text-slate-600"
        >
          Industry
        </label>
        <select
          id={tid('industry-filter')}
          value={industryValue}
          onChange={(e) => onIndustryChange?.(e.target.value)}
          className={`${SELECT_CLASS} min-w-[160px]`}
          data-testid={tid('industry-filter')}
        >
          <option value="all">All industries</option>
          {industries.map((ind) => (
            <option key={ind.id} value={ind.id}>
              {ind.name}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-2">
        <label
          htmlFor={tid('region-filter')}
          className="text-xs font-medium text-slate-600"
        >
          Region
        </label>
        <select
          id={tid('region-filter')}
          value={regionValue}
          onChange={(e) => onRegionChange?.(e.target.value)}
          className={`${SELECT_CLASS} min-w-[140px]`}
          disabled={availableRegions.length === 0}
          data-testid={tid('region-filter')}
        >
          <option value="all">All regions</option>
          {availableRegions.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-2">
        <label
          htmlFor={tid('size-filter')}
          className="text-xs font-medium text-slate-600"
        >
          Company size
        </label>
        <select
          id={tid('size-filter')}
          value={sizeValue}
          onChange={(e) => onSizeChange?.(e.target.value)}
          className={`${SELECT_CLASS} min-w-[140px]`}
          disabled={availableSizes.length === 0}
          data-testid={tid('size-filter')}
        >
          <option value="all">All sizes</option>
          {availableSizes.map((s) => (
            <option key={s} value={s}>
              {sizeLabel(s)}
            </option>
          ))}
        </select>
      </div>

      {anyActive && (
        <button
          type="button"
          onClick={clear}
          className="text-[11px] text-slate-500 hover:text-slate-800 underline"
          data-testid={tid('filters-clear')}
        >
          Clear filters
        </button>
      )}
    </div>
  );
}

export default OrgFilterBar;
